import type { Ref } from 'react';

import type { LegalDocumentSection } from 'services/legal-document-content';
import styles from './LegalDocumentSheet.module.scss';

/**
 * One section of the document, and the only one rendered while it is open.
 *
 * Its neighbours are absent from the DOM rather than scrolled out of view,
 * which is what makes the back control a navigation step instead of a jump.
 */
export const DocumentSection = ({
  section,
  headingRef,
}: {
  section: LegalDocumentSection;
  headingRef: Ref<HTMLHeadingElement>;
}) => (
  <div>
    {/* Focused on entry so keyboard users land on the section, not the chrome. */}
    <h2 className={styles.sectionHeading} tabIndex={-1} ref={headingRef}>
      {section.heading}
    </h2>

    {section.blocks.map((block, index) =>
      block.kind === 'table' ? (
        /*
         * Rows stacked as labelled pairs rather than a grid. These tables are
         * three columns of prose in a 480px sheet, where a real table is
         * either unreadably narrow or needs horizontal scrolling — and a
         * disclosure nobody can read is the thing this is fixing.
         */
        <div key={`table-${index}`} className={styles.table}>
          {block.rows.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className={styles.tableRow}>
              {row.map((lines, cellIndex) => (
                <div key={`cell-${cellIndex}`} className={styles.tableCell}>
                  {block.headers[cellIndex] && (
                    <span className={styles.tableLabel}>
                      {block.headers[cellIndex]}
                    </span>
                  )}
                  {lines.length > 1 ? (
                    <ul className={styles.list}>
                      {lines.map((line) => (
                        <li key={line} className={styles.listItem}>
                          {line}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span>{lines[0] ?? ''}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : block.kind === 'list' ? (
        <ul
          // Blocks have no identity and never reorder, so the index is honest.
          key={`list-${index}`}
          className={styles.list}
        >
          {block.items.map((item) => (
            <li key={item} className={styles.listItem}>
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p key={`p-${index}`} className={styles.paragraph}>
          {block.text}
        </p>
      ),
    )}
  </div>
);
